import { useState } from "react";
import { Link } from "react-router-dom";
import { Instagram, Facebook, Phone, Mail, MapPin } from "lucide-react";

const PinterestIcon = ({ size = 17 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="10" /><path d="M8.5 20.5 11 9.5" /><path d="M10.2 13.8c.5 1 1.6 1.7 2.9 1.7 2.2 0 3.9-1.8 3.9-4.2 0-2.2-1.8-4-4.2-4-2.6 0-4.4 1.9-4.4 4.1 0 .9.3 1.7.9 2.3" />
  </svg>
);
import { useStore } from "@/store/StoreContext";
import api from "@/api";
import { toast } from "sonner";

export default function Footer() {
  const { settings, menus } = useStore();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const store = settings?.store || {};
  const mii = settings?.made_in_india || {};

  const subscribe = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post("/newsletter", { email });
      toast.success(data.message);
      setEmail("");
    } catch (err) {
      toast.error(err.friendlyMessage);
    } finally { setBusy(false); }
  };

  return (
    <footer data-testid="site-footer">
      <div className="bg-[#111111] text-[#FFFFFF]">
        <div className="tq-container pt-20 pb-12 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-10">
          <div className="col-span-2">
            <p className="font-serif text-3xl tracking-[0.12em]">TARISHQ</p>
            <p className="text-[10px] uppercase tracking-[0.5em] text-[#A8821B] mt-1">Fashion</p>
            <p className="text-sm text-[#FFFFFF]/60 mt-5 max-w-xs leading-relaxed">
              Handcrafted luxury footwear for the modern Indian woman. Designed with grace, built for comfort.
            </p>
            <div className="flex gap-3 mt-6">
              {store.social?.instagram && (
                <a href={store.social.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram" data-testid="footer-instagram"
                  className="w-10 h-10 border border-[#FFFFFF]/20 flex items-center justify-center hover:border-[#C59B27] hover:text-[#C59B27] transition-colors">
                  <Instagram size={17} />
                </a>
              )}
              {store.social?.facebook && (
                <a href={store.social.facebook} target="_blank" rel="noopener noreferrer" aria-label="Facebook" data-testid="footer-facebook"
                  className="w-10 h-10 border border-[#FFFFFF]/20 flex items-center justify-center hover:border-[#C59B27] hover:text-[#C59B27] transition-colors">
                  <Facebook size={17} />
                </a>
              )}
              {store.social?.pinterest && (
                <a href={store.social.pinterest} target="_blank" rel="noopener noreferrer" aria-label="Pinterest" data-testid="footer-pinterest"
                  className="w-10 h-10 border border-[#FFFFFF]/20 flex items-center justify-center hover:border-[#C59B27] hover:text-[#C59B27] transition-colors">
                  <PinterestIcon size={17} />
                </a>
              )}
            </div>
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#A8821B] mb-5">Shop</p>
            <ul className="space-y-3 text-sm text-[#FFFFFF]/70">
              <li><Link to="/collections/new-arrivals" className="hover:text-[#C59B27] transition-colors">New Arrivals</Link></li>
              <li><Link to="/collections/best-sellers" className="hover:text-[#C59B27] transition-colors">Best Sellers</Link></li>
              <li><Link to="/collections/heels" className="hover:text-[#C59B27] transition-colors">Heels</Link></li>
              <li><Link to="/collections/juttis" className="hover:text-[#C59B27] transition-colors">Juttis</Link></li>
              <li><Link to="/collections/kolhapuris" className="hover:text-[#C59B27] transition-colors">Kolhapuris</Link></li>
              <li><Link to="/collections/sale" className="hover:text-[#C59B27] transition-colors">Sale</Link></li>
            </ul>
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#A8821B] mb-5">Help</p>
            <ul className="space-y-3 text-sm text-[#FFFFFF]/70">
              <li><Link to="/pages/track-order" className="hover:text-[#C59B27] transition-colors">Track Order</Link></li>
              <li><Link to="/pages/size-guide" className="hover:text-[#C59B27] transition-colors">Size Guide</Link></li>
              <li><Link to="/pages/shipping-policy" className="hover:text-[#C59B27] transition-colors">Shipping Policy</Link></li>
              <li><Link to="/pages/exchange-return-policy" className="hover:text-[#C59B27] transition-colors">Exchange & Returns</Link></li>
              <li><Link to="/pages/contact-us" className="hover:text-[#C59B27] transition-colors">Contact Us</Link></li>
              <li><Link to="/blog" className="hover:text-[#C59B27] transition-colors">Journal</Link></li>
            </ul>
          </div>
          <div className="col-span-2 md:col-span-1">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#A8821B] mb-5">Stay Close</p>
            <form onSubmit={subscribe} className="flex">
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                data-testid="footer-newsletter-input" placeholder="Your email"
                className="flex-1 min-w-0 bg-transparent border border-[#FFFFFF]/25 px-3 py-2.5 text-sm outline-none focus:border-[#C59B27] placeholder:text-[#FFFFFF]/40" />
              <button type="submit" disabled={busy} data-testid="footer-newsletter-submit"
                className="bg-[#C59B27] text-[#111111] px-4 text-xs font-bold uppercase tracking-wider hover:bg-[#FFFFFF] transition-colors disabled:opacity-50">
                Join
              </button>
            </form>
            <ul className="space-y-2.5 text-sm text-[#FFFFFF]/70 mt-6">
              {store.phone && <li><a href={`tel:${store.phone.replace(/\s/g, "")}`} data-testid="footer-phone" className="flex items-center gap-2 hover:text-[#C59B27] transition-colors"><Phone size={14} /> {store.phone}</a></li>}
              {store.email && <li><a href={`mailto:${store.email}`} data-testid="footer-email" className="flex items-center gap-2 hover:text-[#C59B27] transition-colors break-all"><Mail size={14} /> {store.email}</a></li>}
              {store.address && <li className="flex items-start gap-2"><MapPin size={14} className="mt-1 shrink-0" /> {store.address}</li>}
            </ul>
          </div>
        </div>
        <div className="border-t border-[#FFFFFF]/10">
          <div className="tq-container py-6 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-[#FFFFFF]/50">
            <p>© {new Date().getFullYear()} {store.name || "TARISHQ FASHION"}. All rights reserved.</p>
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#FFFFFF]/40">Secure Payments · Razorpay · COD Available</p>
            {mii.enabled && mii.show_footer !== false && (
              <p data-testid="made-in-india-footer" className="flex items-center gap-2 uppercase tracking-[0.2em] text-[#A8821B]">
                {mii.text || "Proudly Made in India"}
                <span className="inline-block w-4 h-3 rounded-[2px] overflow-hidden border border-[#FFFFFF]/20">
                  <span className="block h-1 bg-[#FF9933]" /><span className="block h-1 bg-white" /><span className="block h-1 bg-[#138808]" />
                </span>
              </p>
            )}
            <div className="flex gap-5">
              <Link to="/pages/privacy-policy" className="hover:text-[#C59B27] transition-colors">Privacy</Link>
              <Link to="/pages/terms-conditions" className="hover:text-[#C59B27] transition-colors">Terms</Link>
              <Link to="/pages/exchange-return-policy" className="hover:text-[#C59B27] transition-colors">Returns</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
